(() => {
  const key = Symbol.for('cascas.homePlayer');
  if (window[key]) return;

  const instances = new Map();
  const libraries = new Map();
  const narrow = window.matchMedia('(max-width: 719px)');
  const touchDelivery = window.matchMedia('(hover: none) and (pointer: coarse)');
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const selector = '[data-cascas-home-player]';

  const absoluteURL = (value) => {
    if (typeof value !== 'string' || !value) return '';
    try {
      return new URL(value, window.location.href).href;
    } catch (_) {
      return '';
    }
  };
  const source = (item) => {
    if (!item || typeof item.url !== 'string') return null;
    const type = item.mime_type || (item.format === 'm3u8' ? 'application/x-mpegURL' : item.format === 'mp4' ? 'video/mp4' : '');
    const src = absoluteURL(item.url);
    return type && src ? { src, type } : null;
  };
  const chooseSources = (asset) => {
    const entries = asset && Array.isArray(asset.sources) ? asset.sources : [];
    const mp4 = entries.filter((entry) => /video\/mp4/i.test(entry.mime_type || '') || entry.format === 'mp4').sort((a, b) => (b.height || 0) - (a.height || 0) || (b.width || 0) - (a.width || 0))[0];
    const native = source(mp4);
    const quality = !touchDelivery.matches && asset && source(asset.qualitySource);
    return { primary: quality || native, fallback: quality ? native : null, native };
  };

  const vendor = (url) => {
    if (typeof window.videojs === 'function') return Promise.resolve(window.videojs);
    if (libraries.has(url)) return libraries.get(url);
    const promise = new Promise((resolve, reject) => {
      if (!url) {
        reject(new Error('Player asset unavailable'));
        return;
      }
      const script = document.createElement('script');
      let timeout;
      const finish = (error) => {
        clearTimeout(timeout);
        script.onload = null;
        script.onerror = null;
        if (error || typeof window.videojs !== 'function') reject(error || new Error('Player asset unavailable'));
        else resolve(window.videojs);
      };
      script.src = url;
      script.async = true;
      script.onload = () => finish();
      script.onerror = () => finish(new Error('Player asset unavailable'));
      timeout = setTimeout(() => finish(new Error('Player asset timed out')), 5000);
      document.head.appendChild(script);
    });
    libraries.set(url, promise);
    return promise;
  };

  const mount = (root) => {
    if (instances.has(root)) return;
    const video = root.querySelector('.cascas-home-player__video');
    const data = root.querySelector('[data-cascas-player-config]');
    if (!video || !data) return;

    let config;
    try {
      config = JSON.parse(data.textContent);
    } catch (_) {
      return;
    }

    const button = root.querySelector('.video-pause');
    const poster = root.querySelector('[data-cascas-player-poster]');
    const introSection = typeof root.closest === 'function' ? root.closest('[data-section-type="cascas-video-hero"]') : null;
    const strings = window.theme && window.theme.strings && window.theme.strings.accessibility || {};
    const labels = config.labels || {};
    const pauseLabel = labels.pause || strings.pause_video || (button && button.textContent.trim());
    const playLabel = labels.play || strings.play_video || pauseLabel;
    const state = {
      root,
      video,
      player: null,
      playerReady: false,
      nativeMode: false,
      awaitingNativeStartup: false,
      disposed: false,
      desired: !reduced.matches,
      explicitPlayback: false,
      manualPause: false,
      inView: true,
      fallbackUsed: false,
      fallbackPending: false,
      started: !video.paused,
      playAttempt: 0,
      returnGeneration: 0,
      returnTimers: [],
      returnActive: false,
      returnPending: false,
      frameShown: false,
      frameCallback: null,
      frameGeneration: 0,
      frameFallbackTimer: null,
      animationFrames: [],
      asset: null,
      sources: null,
      cleanups: [],
      observer: null,
      introObserver: null,
      introEnabled: false,
      introDone: false
    };
    instances.set(root, state);

    const alive = () => !state.disposed && instances.get(root) === state;
    const finishIntro = () => {
      if (!alive() || !state.introEnabled || state.introDone) return;
      state.introDone = true;
      introSection.classList.add('is-visible');
      if (state.introObserver) state.introObserver.disconnect();
    };
    const listen = (target, event, handler) => {
      target.addEventListener(event, handler);
      const cleanup = () => target.removeEventListener(event, handler);
      state.cleanups.push(cleanup);
      return cleanup;
    };
    const gestures = [];
    const clearGestures = () => {
      while (gestures.length) gestures.pop()();
    };
    const mayResume = () => alive() && state.desired && !state.manualPause && !document.hidden && state.inView && (!reduced.matches || state.explicitPlayback);
    const armGestures = () => {
      if (!mayResume() || gestures.length) return;
      const gesture = () => {
        if (!mayResume() || !video.paused) return;
        state.awaitingNativeStartup = false;
        play();
      };
      const options = { passive: true };
      ['click', 'touchstart'].forEach((event) => {
        window.addEventListener(event, gesture, options);
        gestures.push(() => window.removeEventListener(event, gesture, options));
      });
    };
    const stopReturn = () => {
      state.returnGeneration++;
      state.returnActive = false;
      state.returnTimers.forEach(clearTimeout);
      state.returnTimers = [];
    };
    const updateButton = () => {
      if (!button || !alive()) return;
      const label = video.paused ? playLabel : pauseLabel;
      if (label) button.textContent = label;
      button.dataset.state = video.paused ? 'paused' : 'playing';
    };
    const cancelFrame = () => {
      if (state.frameCallback !== null && typeof video.cancelVideoFrameCallback === 'function') video.cancelVideoFrameCallback(state.frameCallback);
      state.frameCallback = null;
      clearTimeout(state.frameFallbackTimer);
      state.frameFallbackTimer = null;
      state.animationFrames.forEach((id) => window.cancelAnimationFrame(id));
      state.animationFrames = [];
    };
    const frameAllowed = (generation) => alive() && generation === state.frameGeneration && !state.frameShown && !document.hidden && !video.paused && video.readyState >= 2 && state.desired && !state.manualPause;
    const revealFrame = (generation) => {
      if (!frameAllowed(generation)) return;
      state.frameShown = true;
      root.dataset.cascasPlayerFrame = 'true';
      cancelFrame();
    };
    const decodedFrames = () => {
      try {
        if (typeof video.getVideoPlaybackQuality === 'function') {
          const quality = video.getVideoPlaybackQuality();
          if (quality && Number.isFinite(quality.totalVideoFrames)) return quality.totalVideoFrames;
        }
      } catch (_) {}
      return Number.isFinite(video.webkitDecodedFrameCount) ? video.webkitDecodedFrameCount : null;
    };
    const afterPaint = (generation) => {
      if (state.animationFrames.length) return;
      state.animationFrames.push(window.requestAnimationFrame(() => {
        if (!alive() || generation !== state.frameGeneration) return;
        state.animationFrames.push(window.requestAnimationFrame(() => {
          if (!alive() || generation !== state.frameGeneration) return;
          state.animationFrames = [];
          revealFrame(generation);
        }));
      }));
    };
    const watchFirstFrame = () => {
      if (!alive() || state.frameShown) return;
      const generation = state.frameGeneration;
      let hasCallback = typeof video.requestVideoFrameCallback === 'function';
      if (hasCallback && state.frameCallback === null) {
        try {
          state.frameCallback = video.requestVideoFrameCallback(() => {
            if (!alive() || generation !== state.frameGeneration) return;
            state.frameCallback = null;
            revealFrame(generation);
          });
        } catch (_) {
          hasCallback = false;
        }
      }
      if (!hasCallback && !video.paused && video.readyState >= 2) afterPaint(generation);
      else if (hasCallback && state.frameFallbackTimer === null && !document.hidden && !video.paused && video.readyState >= 2) {
        const frames = decodedFrames();
        const time = video.currentTime;
        state.frameFallbackTimer = setTimeout(() => {
          if (!alive() || generation !== state.frameGeneration) return;
          state.frameFallbackTimer = null;
          const currentFrames = decodedFrames();
          const advanced = frames !== null && currentFrames !== null ? currentFrames > frames : video.currentTime !== time;
          if (advanced && frameAllowed(generation)) afterPaint(generation);
        }, 750);
      }
    };
    const pause = () => {
      if (!state.frameShown) cancelFrame();
      try {
        if (state.player && !state.player.isDisposed()) state.player.pause();
        else video.pause();
      } catch (_) {}
      updateButton();
    };
    const play = () => {
      if (!alive() || state.manualPause || !state.desired || document.hidden || !state.inView || (reduced.matches && !state.explicitPlayback)) return;
      if (!state.nativeMode && (!state.player || !state.playerReady)) return;
      if (state.awaitingNativeStartup && video.readyState < 2 && !state.explicitPlayback) return;
      watchFirstFrame();
      const attempt = ++state.playAttempt;
      const blocked = () => {
        if (attempt !== state.playAttempt || !mayResume() || !video.paused) return;
        root.dataset.cascasPlaybackBlocked = 'true';
        armGestures();
        updateButton();
      };
      try {
        const result = state.nativeMode ? video.play() : state.player.play();
        if (result && typeof result.catch === 'function') result.catch(blocked);
      } catch (_) {
        blocked();
      }
    };
    const resume = () => {
      if (video.paused) play();
      else if (!document.hidden) watchFirstFrame();
    };
    const startReturn = () => {
      if (state.returnActive || !mayResume()) return;
      state.returnActive = true;
      state.returnPending = false;
      const generation = ++state.returnGeneration;
      const retry = () => {
        if (!alive() || generation !== state.returnGeneration) return;
        if (!mayResume()) { stopReturn(); return; }
        if (video.paused) armGestures();
        resume();
      };
      retry();
      [120, 360, 900, 1800, 3500].forEach((delay) => {
        state.returnTimers.push(setTimeout(retry, delay));
      });
      state.returnTimers.push(setTimeout(() => {
        if (alive() && generation === state.returnGeneration) stopReturn();
      }, 4000));
    };
    const foreground = () => {
      if (document.hidden) {
        state.playAttempt++;
        state.returnPending = false;
        stopReturn();
        return;
      }
      if (touchDelivery.matches && state.nativeMode && state.started) {
        if (!state.desired || state.manualPause || reduced.matches && !state.explicitPlayback) return;
        if (!state.inView) { state.returnPending = true; return; }
        startReturn();
      } else resume();
    };
    const nativeSource = (next) => {
      if (!next) return false;
      if (video.networkState !== 3 && !video.error && (absoluteURL(video.getAttribute('src')) === next.src || absoluteURL(video.currentSrc) === next.src)) return true;
      if (!video.currentSrc && video.networkState !== 3) {
        const declared = Array.from(video.querySelectorAll('source')).find((item) => {
          const media = item.getAttribute('media');
          const type = item.getAttribute('type');
          return (!media || window.matchMedia(media).matches) && (!type || typeof video.canPlayType !== 'function' || video.canPlayType(type));
        });
        if (declared && !video.error && absoluteURL(declared.getAttribute('src')) === next.src) return true;
      }
      video.src = next.src;
      return false;
    };
    const nativeChoice = () => {
      if (!state.sources) return null;
      const primary = state.sources.primary;
      if (primary && /mpegurl/i.test(primary.type) && typeof video.canPlayType === 'function' && video.canPlayType(primary.type)) return primary;
      return state.sources.native || primary;
    };
    const applyFallback = () => {
      state.fallbackPending = false;
      const next = state.sources && state.sources.fallback;
      if (!next) return;
      try {
        if (state.player && state.playerReady && !state.player.isDisposed()) state.player.src(next);
        else if (state.nativeMode) nativeSource(next);
        else {
          state.fallbackPending = true;
          return;
        }
        play();
      } catch (_) {}
    };
    const fallback = () => {
      if (!alive() || state.fallbackUsed || !state.sources || !state.sources.fallback) return;
      state.fallbackUsed = true;
      applyFallback();
    };
    const startNative = () => {
      if (!alive()) return;
      state.nativeMode = true;
      state.fallbackPending = false;
      root.dataset.cascasPlayerNative = 'true';
      root.dataset.cascasPlayerReady = 'true';
      const chosen = nativeChoice();
      state.fallbackUsed = !state.sources || !state.sources.fallback || chosen && chosen.src === state.sources.fallback.src;
      const adopted = nativeSource(chosen);
      state.awaitingNativeStartup = adopted && video.autoplay && !state.started && video.paused && video.readyState < 2 && state.desired && !reduced.matches;
      if (state.awaitingNativeStartup) watchFirstFrame();
      if (state.manualPause || !state.desired || (reduced.matches && !state.explicitPlayback)) pause();
      else resume();
    };
    const assetForViewport = () => narrow.matches && config.mobile && config.mobile.id ? config.mobile : config.desktop || config.mobile;
    const selectAsset = (initial = false) => {
      const asset = assetForViewport();
      if (!asset || state.asset && String(state.asset.id) === String(asset.id)) return;
      state.asset = asset;
      state.sources = chooseSources(asset);
      state.fallbackUsed = false;
      state.fallbackPending = false;
      if (poster && asset.poster && String(poster.dataset.cascasPosterId) !== String(asset.id)) {
        poster.src = asset.poster;
        poster.dataset.cascasPosterId = String(asset.id);
      }
      if (initial) return;
      cancelFrame();
      state.frameGeneration++;
      state.frameShown = false;
      delete root.dataset.cascasPlayerFrame;
      if (state.nativeMode) {
        const chosen = nativeChoice();
        state.fallbackUsed = !state.sources.fallback || chosen && chosen.src === state.sources.fallback.src;
        nativeSource(chosen);
      }
      else if (state.player && state.playerReady) state.player.src(state.sources.primary);
      if (state.desired && !state.manualPause) play();
      else pause();
    };

    if (!video.muted) video.muted = true;
    if (!video.defaultMuted) video.defaultMuted = true;
    if (!video.playsInline) video.playsInline = true;
    if (video.autoplay !== !reduced.matches) video.autoplay = !reduced.matches;
    selectAsset(true);
    listen(video, 'playing', () => {
      if (!alive()) return;
      if (state.manualPause || !state.desired || (reduced.matches && !state.explicitPlayback)) {
        pause();
        return;
      }
      delete root.dataset.cascasPlaybackBlocked;
      root.dataset.cascasPlayerPlaying = 'true';
      state.playAttempt++;
      state.awaitingNativeStartup = false;
      state.started = true;
      clearGestures();
      watchFirstFrame();
      updateButton();
    });
    listen(video, 'pause', () => {
      updateButton();
      if (touchDelivery.matches && state.started) armGestures();
    });
    listen(video, 'error', fallback);
    const startupDataReady = () => {
      if (!alive() || !state.awaitingNativeStartup || video.readyState < 2) return;
      state.awaitingNativeStartup = false;
      resume();
    };
    listen(video, 'loadeddata', startupDataReady);
    listen(video, 'canplay', startupDataReady);
    listen(document, 'visibilitychange', foreground);
    listen(window, 'pageshow', foreground);
    listen(window, 'focus', foreground);

    const mediaListener = (query, handler) => {
      if (typeof query.addEventListener === 'function') {
        query.addEventListener('change', handler);
        state.cleanups.push(() => query.removeEventListener('change', handler));
      } else {
        query.addListener(handler);
        state.cleanups.push(() => query.removeListener(handler));
      }
    };
    mediaListener(narrow, () => selectAsset());
    mediaListener(touchDelivery, () => selectAsset());
    mediaListener(reduced, () => {
      if (reduced.matches) {
        video.autoplay = false;
        state.awaitingNativeStartup = false;
        state.desired = false;
        state.explicitPlayback = false;
        state.returnPending = false;
        stopReturn();
        clearGestures();
        pause();
        finishIntro();
      }
    });

    if (button) listen(button, 'click', (event) => {
      event.preventDefault();
      if (!video.paused) {
        state.manualPause = true;
        state.desired = false;
        state.explicitPlayback = false;
        state.returnPending = false;
        stopReturn();
        clearGestures();
        pause();
      } else {
        state.manualPause = false;
        state.desired = true;
        state.explicitPlayback = true;
        state.awaitingNativeStartup = false;
        play();
      }
    });

    if (!state.started && !reduced.matches) {
      armGestures();
    }

    if ('IntersectionObserver' in window) {
      state.observer = new IntersectionObserver((entries) => {
        if (!alive()) return;
        const entry = entries.find((item) => item.target === root);
        if (!entry) return;
        state.inView = entry.isIntersecting;
        if (state.inView) {
          if (state.returnPending) startReturn();
          else resume();
        } else {
          if (state.returnActive) state.returnPending = true;
          stopReturn();
        }
      });
      state.observer.observe(root);
    }
    state.introEnabled = !!introSection && introSection.classList.contains('animation') && introSection.classList.contains('animation--video-hero');
    if (state.introEnabled) {
      state.introDone = introSection.classList.contains('is-visible');
      if (!state.introDone) {
        if (reduced.matches || !('IntersectionObserver' in window)) finishIntro();
        else {
          const margin = window.matchMedia('(min-width: 720px)').matches ? 200 : 100;
          const threshold = Math.min(margin / introSection.offsetHeight, 0.5);
          state.introObserver = new IntersectionObserver((entries) => {
            if (!alive() || state.introDone) return;
            const entry = entries.find((item) => item.target === introSection);
            if (entry && entry.isIntersecting) finishIntro();
          }, { threshold });
          state.introObserver.observe(introSection);
        }
      }
    }
    updateButton();

    const setupPlayer = () => vendor(config.vendorUrl).then((videojs) => {
      if (!alive()) return;
      const chosen = state.sources && (state.fallbackUsed ? state.sources.fallback : state.sources.primary);
      const initialAssetId = state.asset && String(state.asset.id);
      const options = {
        controls: false,
        autoplay: false,
        muted: true,
        loop: true,
        playsinline: true,
        preload: 'auto',
        fill: true,
        fluid: false,
        responsive: false,
        bigPlayButton: false,
        controlBar: false,
        loadingSpinner: false,
        errorDisplay: false,
        posterImage: false,
        textTrackDisplay: false,
        textTrackSettings: false,
        resizeManager: false,
        liveTracker: false,
        html5: { vhs: { overrideNative: !(videojs.browser && videojs.browser.IS_SAFARI) } },
        sources: chosen ? [chosen] : [],
        plugins: {}
      };
      let player;
      try {
        player = videojs(video, options);
        state.player = player;
        player.on('error', fallback);
        state.cleanups.push(() => player.off('error', fallback));
        player.ready(() => {
          if (!alive() || state.player !== player) return;
          state.playerReady = true;
          root.dataset.cascasPlayerReady = 'true';
          if (state.asset && String(state.asset.id) !== initialAssetId) player.src(state.sources.primary);
          if (state.fallbackPending) applyFallback();
          else if (state.manualPause || !state.desired || (reduced.matches && !state.explicitPlayback)) pause();
          else resume();
        });
      } catch (_) {
        state.player = null;
        state.playerReady = false;
        startNative();
      }
    }).catch(startNative);

    state.dispose = () => {
      if (!alive()) return;
      cancelFrame();
      stopReturn();
      clearGestures();
      state.frameGeneration++;
      state.disposed = true;
      state.cleanups.forEach((cleanup) => cleanup());
      if (state.observer) state.observer.disconnect();
      if (state.introObserver) state.introObserver.disconnect();
      if (state.player && !state.player.isDisposed()) state.player.dispose();
      else video.pause();
      instances.delete(root);
    };

    const preferred = state.sources && state.sources.primary;
    if (preferred && (/video\/mp4/i.test(preferred.type) || /mpegurl/i.test(preferred.type) && typeof video.canPlayType === 'function' && video.canPlayType(preferred.type))) startNative();
    else setupPlayer();
  };

  const scan = (container) => {
    if (container.matches && container.matches(selector)) mount(container);
    container.querySelectorAll(selector).forEach(mount);
  };
  window[key] = { instances };
  scan(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    instances.forEach((state, root) => {
      if (event.target === root || event.target.contains(root)) state.dispose();
    });
  });
})();

